import type { QuizPublic } from '@repo/types/quiz';

import { AlertTriangleIcon, ArrowUpRightIcon, BookmarkCheckIcon, InfoIcon } from 'lucide-react';

import QuizBasicStats from '@/components/features/quizzes/QuizBasicStats';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogBody, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/dialog';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { LoadingSwap } from '@/components/ui/loading-swap';
import { useStartQuizDialog } from '@/hooks/quiz';

interface QuizStartAlertDialogProps {
  quiz: QuizPublic;
  state: [boolean, string | null];
  onStateChange: ([open, quizId]: [boolean, string | null]) => void;
  force?: boolean;
  backTo?: string;
}

export default function QuizStartAlertDialog(quizStartAlertDialogProps: QuizStartAlertDialogProps) {
  const { quiz, force = false } = quizStartAlertDialogProps;

  const { state, actions } = useStartQuizDialog(quizStartAlertDialogProps);
  const { ongoingAttempt } = state;

  const ongoingAttemptDescription =
    ongoingAttempt && !ongoingAttempt.isSameQuiz
      ? `Masz niezakończoną próbę ${ongoingAttempt.quizName ? `quizu ${ongoingAttempt.quizName}` : 'innego quizu'}. Możesz ją najpierw dokończyć.`
      : 'Możesz kontynuować swoją poprzednią próbę tego quizu.';

  return (
    <AlertDialog open={state.open} onOpenChange={actions.onOpenChange}>
      <AlertDialogContent className={'sm:max-w-96'}>
        <AlertDialogBody>
          <AlertDialogHeader className="border-border-primary bg-card border-b">
            <FeaturedIcon size={'lg'}>
              <BookmarkCheckIcon />
            </FeaturedIcon>
            <div className={'flex min-w-0 grow flex-col gap-0.5'}>
              <AlertDialogTitle>Rozpocznij Quiz {quiz.name}</AlertDialogTitle>
              <AlertDialogDescription className={'text-muted-foreground text-xs'}>Jeżeli jesteś pewien, rozpocznij poniższy quiz</AlertDialogDescription>
            </div>
          </AlertDialogHeader>
          <div className={'flex flex-col gap-4 overflow-auto p-4'}>
            <QuizBasicStats quiz={quiz} />

            <p className="text-muted-foreground text-sm">{quiz.desc}</p>
            <Alert
              data-visible={state.isError}
              variant="destructive"
              className={
                'bg-fill-destructive/5 overflow-hidden transition-all duration-400 data-[visible=false]:max-h-0 data-[visible=false]:opacity-0 data-[visible=true]:max-h-40 data-[visible=true]:opacity-100'
              }
            >
              <AlertTriangleIcon />
              <AlertTitle>{state.errorMessage.title}</AlertTitle>
              <AlertDescription>{state.errorMessage.detail}</AlertDescription>
            </Alert>
            <Alert
              data-visible={!!ongoingAttempt}
              className="max-h-40 overflow-hidden opacity-100 transition-all duration-400 data-[visible=false]:max-h-0 data-[visible=false]:opacity-0 data-[visible=true]:max-h-40 data-[visible=true]:opacity-100"
            >
              <InfoIcon />
              <AlertTitle>Masz już rozpoczętą próbę</AlertTitle>
              <AlertDescription>{ongoingAttemptDescription}</AlertDescription>
              <Button className="col-span-2 mt-4" variant={'outline'} size="sm" onClick={actions.goToAttempt}>
                Kontynuuj próbę
              </Button>
            </Alert>
          </div>
        </AlertDialogBody>
        <AlertDialogFooter className="border-border-primary bg-accent dark:bg-card border-t">
          <Button variant={'tertiary'} onClick={() => actions.onOpenChange(false)}>
            Anuluj
          </Button>
          {state.showForceOption ? (
            <AlertDialogAction onClick={() => actions.onSubmit(true)} disabled={state.isPending}>
              <LoadingSwap isLoading={state.isPending} className="flex items-center gap-2">
                Wymuś start <ArrowUpRightIcon />
              </LoadingSwap>
            </AlertDialogAction>
          ) : (
            <Button onClick={() => actions.onSubmit(force)} disabled={state.isPending}>
              <LoadingSwap isLoading={state.isPending} className="flex items-center gap-2">
                Rozpocznij <ArrowUpRightIcon />
              </LoadingSwap>
            </Button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
